import React, { useEffect, useState, useMemo, useRef } from 'react';
import Chart from 'react-apexcharts';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectFundCharts,
  setCurrentAssetClassFilter,
} from 'modules/investments-wip/slice/wipSlice';
import './styles.css';

const HorizontalBarChart = ({
  label,
  categories = [],
  data = [],
  generateColors,
  onFilterClick = () => {},
}) => {
  const fundCharts = useSelector(selectFundCharts);
  const fundChartsRef = useRef(fundCharts);

  useEffect(() => {
    fundChartsRef.current = fundCharts;
  }, [fundCharts]);

  const changeFilter = (newFilter, index) => {
    onFilterClick(newFilter);
  };

  // Memoize series data to prevent unnecessary re-renders
  const seriesData = useMemo(
    () =>
      data.map(({ current, total }, index) => ({
        x: categories[index],
        y: current,
        goals: [
          {
            name: 'Target',
            value: total,
            strokeWidth: 3,
            strokeHeight: 7.5,
            strokeDashArray: 0,
            strokeColor: '#FF4560',
          },
        ],
      })),
    [data, categories]
  );

  // Memoize chart options for performance optimization
  const chartOptions = useMemo(
    () => ({
      xaxis: {
        categories,
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          align: 'left',
          show: true,
          offsetX: -10,
          style: { fontSize: '13px', colors: '#000000' },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      stroke: {
        colors: ['transparent'],
        width: 13,
      },
      chart: {
        type: 'bar',
        stacked: false,
        redrawOnParentResize: true,
        height: '100%',
        events: {
          dataPointSelection: (event, chartContext, config) => {
            const dataPointIndex = config.dataPointIndex;
            const selectedCategory = categories[dataPointIndex];
            changeFilter(selectedCategory, dataPointIndex);
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '28',
          distributed: true,
        },
      },
      grid: {
        row: { padding: 10 },
        padding: { top: 10, bottom: 20, left: 15, right: 15 },
      },
      colors: generateColors ? generateColors(1) : ['#0284c3'],
      legend: { show: false, position: 'right' },
      tooltip: {
        enabled: true,
        y: {
          formatter: (value) => `$${new Intl.NumberFormat().format(value)}`,
          title: { formatter: (seriesName) => seriesName },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val, { dataPointIndex }) => {
          const { current, total } = data[dataPointIndex];
          return `$${(current / 1_000_000).toFixed(1)}M / $${(total / 1_000_000).toFixed(1)}M`;
        },
        style: {
          colors: ['#000000'],
          fontSize: '13px',
          fontWeight: 'bold',
        },
        offsetX: 1500,
        offsetY: -15,
      },
    }),
    [categories, data, generateColors]
  );

  useEffect(() => {
    const addLabelClickHandlers = () => {
      const yAxisLabels = document.querySelectorAll('.apexcharts-yaxis-label');
      yAxisLabels.forEach((label, index) => {
        const tspan = label.querySelector('tspan');
        const labelText = tspan ? tspan.textContent : label.textContent;

        const handleClick = () => changeFilter(labelText, index);

        // Remove previous event listener and set a new one
        label.removeEventListener('click', label._clickHandler);
        label._clickHandler = handleClick;
        label.addEventListener('click', label._clickHandler);
      });
    };

    addLabelClickHandlers();
    const observer = new MutationObserver(addLabelClickHandlers);
    observer.observe(document.querySelector('.apexcharts-svg'), {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [categories]);

  return (
    <div
      className="horizontal-wrap"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {label && <div className="grp-lbl">{label}</div>}

      <div style={{ flex: 1, width: '100%' }}>
        <Chart
          type="bar"
          height="100%"
          width="100%"
          options={{
            ...chartOptions,
            plotOptions: {
              bar: {
                ...chartOptions.plotOptions.bar,
                barHeight: '60%',
              },
            },
          }}
          series={[{ name: 'Allocated', data: seriesData }]}
        />
      </div>
    </div>
  );
};

HorizontalBarChart.propTypes = {
  label: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.string),
  data: PropTypes.arrayOf(
    PropTypes.shape({
      current: PropTypes.number.isRequired,
      total: PropTypes.number.isRequired,
    })
  ),
  generateColors: PropTypes.func,
};

export default HorizontalBarChart;
